import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultWorkflowRepresentationResource,
    useWorkflowRepresentationActions,
    WorkflowRepresentationMutationResourceSchema,
    WorkflowRepresentationPropertyValidation,
} from '../../../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { WorkflowRepresentationContent } from './WorkflowRepresentationContent';

const WorkflowRepresentationOverviewContent = ({
    id,
    displayedInOverview,
}: {
    id: string;
    displayedInOverview: boolean;
}) => {
    const readOnly = useSelector(selectReadOnly);

    const workflowRepresentationActions = useWorkflowRepresentationActions();
    const workflowRepresentationQuery = workflowRepresentationActions.useGetResource(id);
    const updateWorkflowRepresentation = workflowRepresentationActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: WorkflowRepresentationMutationResourceSchema,
        defaultValues: defaultWorkflowRepresentationResource,
        submitHandler: async (data) => {
            await updateWorkflowRepresentation.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: workflowRepresentationQuery.data,
    });

    return (
        <QueryBaseComponent name="WorkflowRepresentation" query={workflowRepresentationQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <WorkflowRepresentationContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const WorkflowRepresentationOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const workflowRepresentationActions = useWorkflowRepresentationActions();

    const parametersQuery = workflowRepresentationActions.useGetAllResources(parentId);

    const resourceType = ResourceType.WORKFLOW_REPRESENTATION;

    return (
        <QueryBaseComponent name="WorkflowRepresentations" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Workflow Representations"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <WorkflowRepresentationOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: WorkflowRepresentationPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
